// Pure mutation resolution (conversational-bridge lane, Deliverable C).
// The dispose step of "LLM proposes, code disposes": a proposed BridgeMutationIntent is
// validated against the ledger's ACTUAL levers, args, constraints, and turns. Unknown
// lever or arg → CLARIFY, NEVER GUESS. No phrase hardcodes, no module-specific constants:
// every bound and candidate comes from ledger data. Pure functions only — no I/O.
import { OBJECTIVE_CONSTRAINT_IDS } from '../ask-helix/contracts/objective-value-bridge';
import {
  CONVERSATION_BRIDGE_LEDGER_SCHEMA_VERSION,
  type BridgeMutationIntent,
  type BridgeScenarioArgSchema,
  type ConversationBridgeLedger,
  type MutationClassification,
} from './conversation-bridge.types';

// ---------------------------------------------------------------------------
// Resolution result contract
// ---------------------------------------------------------------------------

// Recompute scope per mutation kind — the design-doc section 5 table, verbatim.
export type BridgeRecomputeScope =
  | Readonly<{
      kind: 'redispatch_levers';
      leverFamilyIds: readonly string[];
      resumPackage: true;
    }>
  | Readonly<{ kind: 'deconfliction_recheck_and_resum' }>
  | Readonly<{ kind: 'dispatch_added_lever_and_deconflict' }>
  | Readonly<{ kind: 'filtered_reselection' }>
  | Readonly<{ kind: 'read_only' }>
  | Readonly<{ kind: 'replay_log' }>;

export type BridgeMutationClarifyCandidate = Readonly<{
  leverFamilyId: string;
  label: string;
  disposition: 'priced' | 'withheld';
}>;

export type BridgeMutationResolution =
  | Readonly<{
      outcome: 'execute';
      mutation: BridgeMutationIntent;
      recomputeScope: BridgeRecomputeScope;
      affectedLeverFamilyIds: readonly string[];
    }>
  // Clarify names the candidate interpretations from the ledger. A clarify turn writes
  // NOTHING to the mutation log — the log records only applied mutations (store rule).
  | Readonly<{
      outcome: 'clarify';
      clarifyPrompt: string;
      candidates: readonly BridgeMutationClarifyCandidate[];
    }>
  // Honest refusal: the ask is understood but not modelable yet. No interpolation, no
  // scaling of snapshot numbers to fake a scenario.
  | Readonly<{ outcome: 'unsupported_honest'; honestReason: string }>
  // Fail-closed paths: never mutate against a ledger you cannot trust.
  | Readonly<{
      outcome: 'fresh_question';
      reason: 'no_mutation_matched' | 'ledger_expired' | 'ledger_schema_mismatch';
    }>;

export type ResolveBridgeMutationOptions = Readonly<{
  // ISO instant used for the TTL check; defaults to the wall clock.
  now?: string;
  // Below this confidence the resolver clarifies instead of executing (default 0.8).
  confidenceThreshold?: number;
}>;

const DEFAULT_CONFIDENCE_THRESHOLD = 0.8;

// ---------------------------------------------------------------------------
// Recompute-scope table
// ---------------------------------------------------------------------------

export function recomputeScopeForMutation(
  mutation: BridgeMutationIntent,
): BridgeRecomputeScope {
  switch (mutation.kind) {
    case 'change_scenario_arg':
      return {
        kind: 'redispatch_levers',
        leverFamilyIds: [mutation.leverFamilyId],
        resumPackage: true,
      };
    case 'remove_lever':
      // No dispatch: removing a lever can release a suppressed overlap claim, so the
      // fixed-key de-confliction is re-checked before the package re-sums.
      return { kind: 'deconfliction_recheck_and_resum' };
    case 'add_lever':
      return { kind: 'dispatch_added_lever_and_deconflict' };
    case 'add_constraint':
    case 'remove_constraint':
      return { kind: 'filtered_reselection' };
    case 'explain_withhold':
    case 'compare_states':
      return { kind: 'read_only' };
    case 'reset_to':
      return { kind: 'replay_log' };
    default:
      return assertNever(mutation);
  }
}

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

export function resolveBridgeMutation(
  classification: MutationClassification,
  ledger: ConversationBridgeLedger,
  opts?: ResolveBridgeMutationOptions,
): BridgeMutationResolution {
  // Rule 1 — no mutation matched: route as a fresh question.
  const mutation = classification.matched;
  if (mutation === null) {
    return { outcome: 'fresh_question', reason: 'no_mutation_matched' };
  }

  // Rule 2 — schema-version mismatch: fail closed, never mutate an untrusted ledger.
  if ((ledger.schemaVersion as string) !== CONVERSATION_BRIDGE_LEDGER_SCHEMA_VERSION) {
    return { outcome: 'fresh_question', reason: 'ledger_schema_mismatch' };
  }

  // Rule 3 — TTL: an expired ledger is untrusted working state.
  const nowMs = Date.parse(opts?.now ?? new Date().toISOString());
  if (!Number.isNaN(nowMs) && nowMs >= Date.parse(ledger.expiresAt)) {
    return { outcome: 'fresh_question', reason: 'ledger_expired' };
  }

  const candidates = ledgerLeverCandidates(ledger);

  // Rule 4 — low confidence: clarify with the ledger's lever list, never guess.
  const threshold = opts?.confidenceThreshold ?? DEFAULT_CONFIDENCE_THRESHOLD;
  if (classification.confidence < threshold) {
    return clarify(
      classification.clarifyPrompt ??
        `I want to be sure before changing the bridge — which of these did you mean: ${
          candidates.map((candidate) => candidate.label).join(', ')
        }?`,
      candidates,
    );
  }

  switch (mutation.kind) {
    case 'change_scenario_arg':
      return resolveChangeScenarioArg(mutation, ledger, candidates);
    case 'remove_lever':
    case 'explain_withhold':
    case 'add_lever':
      return resolveLeverMutation(mutation, ledger, candidates);
    case 'add_constraint': {
      // Rule 7 — closed constraint vocabulary: invented constraint ids are invisible to
      // the overlap keys and must never pass.
      const constraintId = mutation.constraint.constraintId;
      if (!OBJECTIVE_CONSTRAINT_IDS.includes(constraintId)) {
        return clarify(
          `"${String(constraintId)}" is not a constraint I can enforce. Supported ` +
            `constraints: ${OBJECTIVE_CONSTRAINT_IDS.join(', ')}.`,
          candidates,
        );
      }
      return execute(mutation, []);
    }
    case 'remove_constraint': {
      // Rule 8 — the constraint must actually be applied before it can be removed.
      const applied = ledger.appliedConstraints.some(
        (constraint) => constraint.constraintId === mutation.constraintId,
      );
      if (!applied) {
        const appliedIds = ledger.appliedConstraints
          .map((constraint) => constraint.constraintId)
          .join(', ');
        return clarify(
          `"${mutation.constraintId}" is not an applied constraint. Currently applied: ` +
            `${appliedIds.length > 0 ? appliedIds : 'none'}.`,
          candidates,
        );
      }
      return execute(mutation, []);
    }
    case 'compare_states': {
      // Rule 10 — both turns must be addressable (turn 1 = the initial bridge).
      const validTurns = addressableTurnIndices(ledger);
      const badTurn = [mutation.fromTurn, mutation.toTurn].find(
        (turn) => !validTurns.has(turn),
      );
      if (badTurn !== undefined) {
        return clarify(turnClarifyPrompt(badTurn, validTurns), candidates);
      }
      return execute(mutation, []);
    }
    case 'reset_to': {
      // Rule 10 — the reset target must exist in the addressable turn set.
      const validTurns = addressableTurnIndices(ledger);
      if (!validTurns.has(mutation.turnIndex)) {
        return clarify(turnClarifyPrompt(mutation.turnIndex, validTurns), candidates);
      }
      return execute(mutation, []);
    }
    default:
      return assertNever(mutation);
  }
}

// ---------------------------------------------------------------------------
// Per-kind resolution helpers
// ---------------------------------------------------------------------------

function resolveChangeScenarioArg(
  mutation: Extract<BridgeMutationIntent, { kind: 'change_scenario_arg' }>,
  ledger: ConversationBridgeLedger,
  candidates: readonly BridgeMutationClarifyCandidate[],
): BridgeMutationResolution {
  // Rule 5 — the lever must exist in the last bridge (priced or withheld).
  const known = candidates.some(
    (candidate) => candidate.leverFamilyId === mutation.leverFamilyId,
  );
  if (!known) {
    return unknownLeverClarify(mutation.leverFamilyId, candidates);
  }

  // Rule 6 — no dispatch entry or null tool id = no scenario wiring: answer honestly,
  // never interpolate or scale snapshot numbers.
  const dispatch = ledger.dispatchState[mutation.leverFamilyId];
  if (dispatch === undefined || dispatch.scenarioToolId === null) {
    return {
      outcome: 'unsupported_honest',
      honestReason:
        `I can model changes to ${mutation.leverFamilyId} once its scenario contract ` +
        'lands — it is currently priced from the approved snapshot only.',
    };
  }

  // Rule 6 — the arg must exist in the lever's dispatched schema; clarify names the
  // known args instead of guessing an adjacent one.
  const argSchema = dispatch.argSchema[mutation.argName];
  if (argSchema === undefined) {
    const knownArgs = Object.keys(dispatch.argSchema).join(', ');
    return clarify(
      `"${mutation.argName}" is not an argument of ${mutation.leverFamilyId}. Known ` +
        `arguments: ${knownArgs.length > 0 ? knownArgs : 'none'}.`,
      candidates,
    );
  }

  // Rule 6 — the value must satisfy the typed bounds from the ledger's arg schema.
  const violation = argValueViolation(argSchema, mutation.newValue);
  if (violation !== null) {
    return clarify(
      `${mutation.newValue} is not a valid value for ` +
        `${mutation.leverFamilyId}.${mutation.argName}: ${violation}`,
      candidates,
    );
  }

  return execute(mutation, [mutation.leverFamilyId]);
}

function resolveLeverMutation(
  mutation: Extract<
    BridgeMutationIntent,
    { kind: 'remove_lever' | 'add_lever' | 'explain_withhold' }
  >,
  ledger: ConversationBridgeLedger,
  candidates: readonly BridgeMutationClarifyCandidate[],
): BridgeMutationResolution {
  const priced = ledger.lastBridge.leverRefs.some(
    (lever) => lever.leverFamilyId === mutation.leverFamilyId,
  );
  const withheld = ledger.lastBridge.withheldLeverRefs.some(
    (lever) => lever.leverFamilyId === mutation.leverFamilyId,
  );

  // Rule 5 — unknown lever → clarify listing nearest candidates, never guess.
  if (!priced && !withheld) {
    return unknownLeverClarify(mutation.leverFamilyId, candidates);
  }

  // Rule 9 — explain_withhold only reads recorded withhold reasons; a priced lever has
  // none, so the resolver explains it is already in the package.
  if (mutation.kind === 'explain_withhold' && !withheld) {
    return clarify(
      `${mutation.leverFamilyId} is not withheld — it is priced in the current ` +
        'package. Did you mean one of the withheld levers?',
      candidates.filter((candidate) => candidate.disposition === 'withheld'),
    );
  }

  // Rule 11 — add_lever on an already-priced lever is a no-op ask; clarify instead.
  if (mutation.kind === 'add_lever' && priced) {
    return clarify(
      `${mutation.leverFamilyId} is already priced in the current package. Did you ` +
        'mean a different lever?',
      candidates,
    );
  }

  return execute(mutation, [mutation.leverFamilyId]);
}

// ---------------------------------------------------------------------------
// Generic helpers (data-driven — no phrase or module hardcodes)
// ---------------------------------------------------------------------------

function execute(
  mutation: BridgeMutationIntent,
  affectedLeverFamilyIds: readonly string[],
): BridgeMutationResolution {
  return {
    outcome: 'execute',
    mutation,
    recomputeScope: recomputeScopeForMutation(mutation),
    affectedLeverFamilyIds,
  };
}

function clarify(
  clarifyPrompt: string,
  candidates: readonly BridgeMutationClarifyCandidate[],
): BridgeMutationResolution {
  return { outcome: 'clarify', clarifyPrompt, candidates };
}

function ledgerLeverCandidates(
  ledger: ConversationBridgeLedger,
): readonly BridgeMutationClarifyCandidate[] {
  return [
    ...ledger.lastBridge.leverRefs.map((lever) => ({
      leverFamilyId: lever.leverFamilyId,
      label: lever.label,
      disposition: 'priced' as const,
    })),
    ...ledger.lastBridge.withheldLeverRefs.map((lever) => ({
      leverFamilyId: lever.leverFamilyId,
      label: lever.label,
      disposition: 'withheld' as const,
    })),
  ];
}

// Nearest-candidate suggestion is generic case-insensitive containment over ids and
// labels — string logic only, no phrase hardcodes. Falls back to the full candidate set.
function unknownLeverClarify(
  requestedLeverFamilyId: string,
  candidates: readonly BridgeMutationClarifyCandidate[],
): BridgeMutationResolution {
  const needle = requestedLeverFamilyId.toLowerCase();
  const near = candidates.filter((candidate) => {
    const id = candidate.leverFamilyId.toLowerCase();
    const label = candidate.label.toLowerCase();
    return (
      id.includes(needle) || needle.includes(id) ||
      label.includes(needle) || needle.includes(label)
    );
  });
  const suggested = near.length > 0 ? near : candidates;
  const named = suggested
    .map((candidate) => `${candidate.label} (${candidate.disposition})`)
    .join(', ');
  return clarify(
    `I don't have a lever "${requestedLeverFamilyId}" in this bridge. Did you mean: ` +
      `${named}?`,
    suggested,
  );
}

// Returns null when the value satisfies the schema, else a bounds message for the
// clarify prompt (the bounds come from ledger data, never constants in this code).
function argValueViolation(
  schema: BridgeScenarioArgSchema,
  value: number | string,
): string | null {
  if (schema.kind === 'number') {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return 'expected a finite number.';
    }
    if (schema.min !== undefined && value < schema.min) {
      return boundsMessage(schema);
    }
    if (schema.max !== undefined && value > schema.max) {
      return boundsMessage(schema);
    }
    return null;
  }
  if (typeof value !== 'string') {
    return 'expected a string value.';
  }
  if (schema.allowedValues !== undefined && !schema.allowedValues.includes(value)) {
    return `allowed values are ${schema.allowedValues.join(', ')}.`;
  }
  return null;
}

function boundsMessage(
  schema: Extract<BridgeScenarioArgSchema, { kind: 'number' }>,
): string {
  const min = schema.min !== undefined ? String(schema.min) : 'unbounded';
  const max = schema.max !== undefined ? String(schema.max) : 'unbounded';
  const unit = schema.unit !== undefined ? ` ${schema.unit}` : '';
  return `value must be between ${min} and ${max}${unit}.`;
}

// Turn 1 (the initial bridge) is always addressable; every applied mutation adds its
// turn index. Clarify prompts name the addressable turns from this set.
function addressableTurnIndices(ledger: ConversationBridgeLedger): Set<number> {
  const turns = new Set<number>([1]);
  for (const entry of ledger.mutationLog) {
    turns.add(entry.turnIndex);
  }
  return turns;
}

function turnClarifyPrompt(badTurn: number, validTurns: Set<number>): string {
  const named = [...validTurns].sort((a, b) => a - b).join(', ');
  return `Turn ${badTurn} does not exist in this conversation. Addressable turns: ${named}.`;
}

function assertNever(value: never): never {
  throw new Error(`Unhandled bridge mutation kind: ${JSON.stringify(value)}`);
}
